/**
 * React component representing the section for staying updated with mailing list and social media links.
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.data - Data for rendering the stay updated section.
 * @returns {JSX.Element} The rendered StayUpdatedSection component.
 */

import React, { useEffect, useContext, useState } from "react";
import styles from "./StayUpdatedSection.module.scss";
import commonstyles from "../../../common/commonstyles.module.scss";
import Instagram from "../../../assets/svg/footerSvgs/Instagram";
import Twitter from "../../../assets/svg/footerSvgs/Twitter";
import Linkedin from "../../../assets/svg/footerSvgs/Linkedin";
import Link from "next/link";
import axios from "axios";
import { Button } from "../common";
import { BTN_TYPES, PLAUSIBLE_EVENTS } from "@/utils/constants";
import { Plausible } from "@/providers/PlausibleProvider";

const StayUpdatedSection = ({ data }) => {
  const [email, setEmail] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [isEmailProcessing, setIsEmailProcessing] = useState(false);
  const [showBackground, setShowBackground] = useState(false);

  useEffect(() => {
    if (window.location.hash === "#join-our-mailing-list") {
      setShowBackground(true);
    }
  }, []);

  const { plausibleEvent } = useContext(Plausible);

  const handleInputChange = (event) => {
    if (errMsg?.length > 0) {
      setErrMsg("");
    }
    setEmail(event.target.value);
    // Check if the entered email is valid
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setIsValidEmail(emailRegex.test(event.target.value));
  };

  const apiUrl = `${process.env.NEXT_PUBLIC_WEB_BASE_URL}/keep-me-posted`;

  const sendEmail = async () => {
    setIsEmailProcessing(true);
    setErrMsg("");
    setShowBackground(false);
    try {
      const response = await axios.post(apiUrl, { email });
      const data = response?.data;
      setSuccessMsg("You’re on the list!");
      setIsEmailProcessing(false);
      plausibleEvent(PLAUSIBLE_EVENTS.MAILING_SIGNUP);

      setTimeout(() => {
        setSuccessMsg("");
        setEmail("");
      }, 5000);

      if (!data?.success) {
        const err = data?.error?.error_data?.[0]?.msg;
        setIsEmailProcessing(false);
        setErrMsg(err);
      } else {
        localStorage.setItem("isSubscribed", "true");
      }
    } catch (error) {
      setIsEmailProcessing(false);
      setErrMsg(error.response?.data?.error?.error_data?.[0]?.msg);
    }
  };

  const handleSubmit = () => {
    if (isValidEmail) {
      sendEmail();
    } else {
      setIsEmailProcessing(false);
      setErrMsg("The login information you entered is invalid");
    }
  };

  return (
    <div className="flex flex-col justify-center items-center lg:py-[66px] pt-[64px] pb-[40px]">
      <div
        className={`${commonstyles.mg_normal_24} text-white lg:pb-[8px] pb-[5px] max-w-[240px] lg:max-w-[310px] text-center`}
      >
        {data?.stayUpToDateTitle}
      </div>
      <div
        className={`max-w-[326px] text-center text-white lg:pb-[24px] pb-[16px] ${commonstyles.mg_normal_18}`}
      >
        {data?.specialOffersAndNewProductUpdates}
      </div>
      <div className="px-[25px] lg:px-0 w-full lg:w-auto">
        {successMsg?.length > 0 ? (
          <h6 className="text-white flex justify-center items-center h-[66.4px] lg:h-[74.2px]">
            {successMsg}
          </h6>
        ) : (
          <div
            className={`${styles.emaildiv} flex justify-between lg:justify-center items-center p-[8px]`}
          >
            <input
              type="text"
              className={`ml-[30px] w-[208px] lg:block hidden outline-none ${styles.inputDiv} ${commonstyles.mg_normal_18}`}
              value={email}
              placeholder={data?.enterYourEmailAddressText}
              onChange={handleInputChange}
            />
            <input
              type="text"
              className={`lg:hidden block outline-none ${styles.inputDiv} eyebrow-bold px-[15px] overflow-hidden w-full normal-case font-ppMori tracking-normal`}
              value={email}
              placeholder={data?.enterYourEmailAddressMobileText}
              onChange={handleInputChange}
            />
            {/* <button
                type="submit"
                onClick={handleSubmit}
                className={`btn-secondary eyebrow-bold lg:ml-[50px] group-disabled:text-neutral`}
                disabled={isEmailProcessing}
              >
                {isEmailProcessing ? "processing" : data?.submitCtaText}
              </button> */}
            <div className="lg:ml-[50px]">
              <Button
                onClickFn={handleSubmit}
                btnType={BTN_TYPES.SECONDARY}
                disabled={isEmailProcessing}
              >
                <div
                  className={`eyebrow-bold  group-hover:text-white group-disabled:text-white ${
                    showBackground ? "bg-[#e9d3fd]" : ""
                  }`}
                >
                  {isEmailProcessing ? "processing" : data?.submitCtaText}
                </div>
              </Button>
            </div>
          </div>
        )}
      </div>
      {errMsg?.length > 0 && (
        <div className="text-error small-p h-[20px] py-[16px] px-[25px] lg:px-0">
          {errMsg}
        </div>
      )}
      <div className="flex gap-[24px] mt-[24px]">
        <Link
          href="https://twitter.com/maticrobots"
          title="Matic - Twitter"
          target="_blank"
        >
          <Twitter />
        </Link>
        <Link
          href="https://www.instagram.com/maticrobots/"
          title="Matic - Instagram"
          target="_blank"
        >
          <Instagram />
        </Link>
        <Link
          href="https://www.linkedin.com/company/matician"
          title="Matic - Linkedin"
          target="_blank"
        >
          <Linkedin />
        </Link>
      </div>
    </div>
  );
};

export default StayUpdatedSection;
